import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Calendar, Home, Loader2, Paperclip, Download, FileText, File as FileIcon, FileSpreadsheet, FileImage } from "lucide-react";

export default function PublicArticlePage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, article: null, error: "" });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, article: null, error: "" });
    api.get(`/public/articles/${encodeURIComponent(slug)}`)
      .then(({ data }) => { if (!cancelled) setState({ loading: false, article: data, error: "" }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, article: null, error: formatApiError(e) }); });
    return () => { cancelled = true; };
  }, [slug]);

  const { loading, article, error } = state;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="public-article-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="article-back-home">
            <div className="h-9 w-9 rounded-md bg-slate-900 text-white grid place-items-center flex-shrink-0">
              <Home className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display font-bold tracking-tight text-sm group-hover:text-slate-700">SIPILKADES</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Desa Mekarsari</div>
            </div>
          </Link>
          <Link
            to="/"
            className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Beranda
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 md:px-6 py-6 md:py-10">
        {loading && (
          <div className="flex items-center justify-center py-20 text-slate-400" data-testid="article-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
        {error && !loading && (
          <div className="text-center py-20" data-testid="article-error">
            <div className="text-slate-900 font-display text-2xl font-bold">Artikel tidak ditemukan</div>
            <div className="text-slate-500 text-sm mt-2">{error}</div>
            <Link to="/" className="inline-flex items-center gap-1 mt-6 text-sm font-semibold text-slate-900 underline">
              <ArrowLeft className="h-4 w-4" /> Kembali ke Beranda
            </Link>
          </div>
        )}
        {article && !loading && (
          <article className="space-y-5" data-testid="article-content">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">Info Terbaru</div>
              <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight mt-2">
                {article.title}
              </h1>
              <div className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {new Date(article.display_date || article.created_at).toLocaleDateString("id-ID", {
                  weekday: "long", day: "numeric", month: "long", year: "numeric",
                })}
              </div>
            </div>

            {article.cover_url && (
              <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center" data-testid="article-cover">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${article.cover_url}`}
                  alt={article.title}
                  className="w-full h-auto max-h-[80vh] object-contain"
                  loading="lazy"
                />
              </div>
            )}

            {/* Preserve line breaks — server stores plain text */}
            <div
              className="prose prose-slate max-w-none text-[15px] md:text-base leading-relaxed text-slate-800 whitespace-pre-wrap break-words"
              data-testid="article-body"
            >
              {article.body}
            </div>

            {/* Attachments */}
            {article.attachments && article.attachments.length > 0 && (
              <div className="pt-4" data-testid="article-attachments">
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold flex items-center gap-1.5 mb-2">
                  <Paperclip className="h-3 w-3" />
                  Lampiran
                </div>
                <ul className="space-y-2">
                  {article.attachments.map((a) => (
                    <li key={a.file_id}>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${a.download_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 rounded-md border border-slate-200 bg-white px-3 py-2.5 hover:border-slate-900 hover:bg-slate-50 transition"
                        data-testid={`article-attachment-${a.file_id}`}
                      >
                        <div className="h-9 w-9 flex-shrink-0 rounded-md bg-slate-100 grid place-items-center text-slate-600">
                          {attachmentIcon(a.content_type, a.filename)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-slate-900 truncate">{a.filename}</div>
                          <div className="text-[11px] text-slate-500">{formatBytes(a.size)}</div>
                        </div>
                        <Download className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="pt-6 mt-6 border-t border-slate-200">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 hover:text-slate-600"
              >
                <ArrowLeft className="h-4 w-4" /> Kembali ke Beranda
              </Link>
            </div>
          </article>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-4 text-xs text-slate-500 flex items-center justify-between">
          <span>© 2026 Desa Mekarsari</span>
          <span className="uppercase tracking-[0.2em]">SIPILKADES · Sistem Resmi</span>
        </div>
      </footer>
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes) return "-";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

function attachmentIcon(contentType, filename) {
  const ct = (contentType || "").toLowerCase();
  const name = (filename || "").toLowerCase();
  if (ct.includes("pdf") || name.endsWith(".pdf")) return <FileText className="h-4 w-4 text-red-600" />;
  if (ct.includes("spreadsheet") || ct.includes("excel") || name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv")) return <FileSpreadsheet className="h-4 w-4 text-emerald-700" />;
  if (ct.includes("image") || /\.(jpg|jpeg|png|webp|gif)$/.test(name)) return <FileImage className="h-4 w-4 text-blue-600" />;
  if (ct.includes("word") || name.endsWith(".doc") || name.endsWith(".docx")) return <FileText className="h-4 w-4 text-blue-700" />;
  return <FileIcon className="h-4 w-4 text-slate-500" />;
}
